import React, { useEffect, useState } from "react";
import { FaSpinner, FaEye } from "react-icons/fa";
import { FaXmark, FaTowerBroadcast } from "react-icons/fa6";
import * as webGmBridge from "../../live/webGmBridge";
import { useGmLive } from "../../live/useGmLive";
import { queueEvent } from "../../analytics";
import { EVENTS } from "../../analytics/events";

// Web game master's "live session" modal: explain → consent (private custom
// modes) → live (QR + link + per-team viewer counts + stop). Same flow as
// the app's LiveModal.
const GmLiveModal = ({ open, onClose, game }) => {
  const live = useGmLive();
  const [phase, setPhase] = useState("explain"); // explain | consent | confirmStop
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasPrivateMode = !!game?.modes?.some((m) => m.custom);

  useEffect(() => {
    if (!open) setPhase("explain");
  }, [open]);

  if (!open) return null;

  const doStart = async () => {
    setError(null);
    setLoading(true);
    try {
      await webGmBridge.startLive(
        game?.codeInfo?.code || game?._id,
        webGmBridge.buildBoardState(game)
      );
      setPhase("explain");
    } catch {
      setError("تعذر بدء البث، حاول مرة أخرى");
    } finally {
      setLoading(false);
    }
  };

  const onStartPress = () => {
    if (hasPrivateMode) setPhase("consent");
    else doStart();
  };

  const onStop = () => {
    webGmBridge.stopLive("ended");
    setPhase("explain");
    onClose();
  };

  const copyLink = () => {
    try {
      navigator.clipboard?.writeText(live.url);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#000000a0] p-4"
      style={{ direction: "rtl" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative flex flex-col items-center gap-4 max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 left-3 bg-danger text-white rounded-full p-2 hover:opacity-80"
        >
          <FaXmark />
        </button>

        {live.active ? (
          <>
            <div className="flex items-center gap-2 text-header font-bold text-xl">
              <span className="w-3 h-3 rounded-full bg-danger animate-pulse" />
              البث مباشر الآن
            </div>
            {live.qrDataUrl && (
              <img
                src={live.qrDataUrl}
                alt="QR"
                className="w-44 h-44 rounded-lg border-4 border-behindButton"
              />
            )}
            <button
              onClick={copyLink}
              className="text-secondaryButton text-sm underline"
              title="نسخ الرابط"
            >
              {live.url?.replace("https://", "")}
            </button>
            <div className="flex items-center gap-2 text-dark font-bold">
              <FaEye /> المشاهدون: {live.presence.total}
            </div>
            <div className="flex flex-row flex-wrap justify-center gap-2">
              {(game?.teams || []).map((t) => {
                const id = webGmBridge.teamId(t);
                return (
                  <span
                    key={id}
                    className="bg-behindButton text-white text-sm rounded-full px-3 py-1"
                  >
                    {t.name}: {live.presence.byTeam[id] || 0}
                  </span>
                );
              })}
            </div>
            {phase === "confirmStop" ? (
              <div className="flex gap-2">
                <button
                  onClick={onStop}
                  className="bg-danger text-white font-bold rounded-xl px-5 py-2"
                >
                  تأكيد الإيقاف
                </button>
                <button
                  onClick={() => setPhase("explain")}
                  className="bg-secondaryButton text-white font-bold rounded-xl px-5 py-2"
                >
                  تراجع
                </button>
              </div>
            ) : (
              <button
                onClick={() => setPhase("confirmStop")}
                className="bg-danger text-white font-bold rounded-xl px-5 py-2"
              >
                إيقاف البث
              </button>
            )}
          </>
        ) : phase === "consent" ? (
          <>
            <h2 className="text-xl font-bold text-header">تنبيه: فئات خاصة</h2>
            <p className="text-dark text-center">
              تحتوي لعبتك على فئة خاصة. عند بدء البث ستكون أسئلتها مرئية لأي
              شخص يملك الرابط. هل توافق؟
            </p>
            <div className="flex gap-2">
              <button
                onClick={doStart}
                className="bg-mainButton hover:bg-mainButtonShadow text-white font-bold rounded-xl px-5 py-2"
              >
                موافق، ابدأ البث
              </button>
              <button
                onClick={() => {
                  queueEvent(EVENTS.LIVE_CONSENT_DECLINED);
                  setPhase("explain");
                }}
                className="bg-secondaryButton text-white font-bold rounded-xl px-5 py-2"
              >
                إلغاء
              </button>
            </div>
          </>
        ) : (
          <>
            <FaTowerBroadcast className="text-brown text-4xl" />
            <h2 className="text-xl font-bold text-header">البث المباشر</h2>
            <p className="text-dark text-center">
              شارك لعبتك مباشرة! سيظهر رمز QR يمكن لأي شخص مسحه لمتابعة اللعب،
              والانضمام لتشجيع أحد الفرق والتصويت على اختيار السؤال ووسائل
              المساعدة — القرار النهائي يبقى لك.
            </p>
            {error && <p className="text-danger font-bold">{error}</p>}
            <button
              onClick={onStartPress}
              disabled={loading}
              className="bg-mainButton hover:bg-mainButtonShadow disabled:opacity-60 text-white font-bold rounded-xl px-6 py-3 flex items-center gap-2"
            >
              {loading && <FaSpinner className="animate-spin" />}
              ابدأ البث
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default GmLiveModal;
